import { type ReactNode, useEffect, useMemo } from "react";
import { useLocalStorage } from "usehooks-ts";

import {
  initialMode,
  initialTheme,
  isTheme,
  type SelectedMode,
  THEMES,
  type Theme,
  ThemeProviderContext,
} from "./use-theme";

export interface ThemeProviderProps {
  children: ReactNode;
  defaultSelectedMode?: SelectedMode;
  defaultTheme?: Theme;
  modeStorageKey?: string;
  themeStorageKey?: string;
}

export function ThemeProvider({
  children,
  defaultSelectedMode = initialMode,
  defaultTheme = initialTheme,
  modeStorageKey = "mode",
  themeStorageKey = "theme",
  ...props
}: ThemeProviderProps) {
  const [selectedMode, setSelectedMode] = useLocalStorage<SelectedMode>(modeStorageKey, defaultSelectedMode);
  const [storedTheme, setTheme] = useLocalStorage<Theme>(themeStorageKey, defaultTheme);
  // A stored theme we no longer offer (a retired "orange", a stray "light") renders as the default
  // from the first paint, and the effect below saves that default.
  const theme = isTheme(storedTheme) ? storedTheme : initialTheme;

  const mode = useMemo(() => {
    if (selectedMode === "system") {
      return globalThis.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
    }

    return selectedMode;
  }, [selectedMode]);

  // Repair stored values, legacy "beef" mode first: it sets the theme itself, so the retired-theme
  // fallback only runs once the mode is valid.
  useEffect(() => {
    if (selectedMode === "beef") {
      setSelectedMode("light");
      setTheme("beef");
    } else if (storedTheme !== theme) {
      setTheme(theme);
    }
  }, [selectedMode, storedTheme, theme, setSelectedMode, setTheme]);

  useEffect(() => {
    const root = globalThis.document.documentElement;

    root.classList.remove("light", "dark", ...THEMES);
    root.classList.add(mode, theme);
  }, [mode, theme]);

  const value = useMemo(
    () => ({
      mode,
      theme,
      selectedMode,
      setSelectedMode,
      setTheme,
    }),
    [mode, selectedMode, theme, setTheme, setSelectedMode]
  );

  return (
    <ThemeProviderContext.Provider {...props} value={value}>
      {children}
    </ThemeProviderContext.Provider>
  );
}
